'use client';

import React, { useState, useEffect, useMemo } from 'react';

import Link from 'next/link';
import { useParams } from 'next/navigation';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import useSWR from 'swr';

import DocTree from '@/components/DocTree';
import LiveContext from '@/components/LiveContext';
import { useCompose } from '@/context/ComposeContext';
import { useUserInfo } from '@/context/UserInfoContext';
import { API_URL, ASPECT_REVIEW, ASPECT_QUESTION } from '@/lib/consts';
import { topic_url } from '@/lib/utils';
import { PAGE_PY } from '@/theme/layoutConstants';

import {
  DisplayDoc,
  DisplayConceptRef,
  DocNode,
  PublicDocId,
  ThreadTableOfContents,
  asPublicDocId,
  urlFetcher,
  buildDocTree,
} from '../../../lib/api';

const DocPage: React.FC = () => {
  const { userInfo } = useUserInfo();
  const { setReplyToDocId, setOnComposeAtLatest } = useCompose();
  const params = useParams();
  const id: PublicDocId | undefined = params?.id
    ? asPublicDocId(Array.isArray(params.id) ? params.id[0] : params.id)
    : undefined;
  const {
    data: docs,
    error,
    isLoading,
    mutate,
  } = useSWR<DisplayDoc[]>(id ? `${API_URL}/doc/thread/${id}` : null, urlFetcher);

  const refreshThread = React.useCallback(() => {
    void mutate();
  }, [mutate]);

  // Create a stable key based on doc IDs to detect when thread content changes
  const docsKey = useMemo(() => {
    if (!docs || docs.length === 0) return '';
    // Create a hash of all doc ids in order
    return docs.map((doc) => doc.id).join(',');
  }, [docs]);

  // Fetch table of contents separately (only for logged-in users)
  // Use the docs content hash as the cache key since ToC only depends on the docs
  const tocCacheKey = docs && docs.length >= 3 && userInfo && docsKey ? docsKey : null;

  const {
    data: tableOfContents,
    error: tocError,
    isLoading: tocLoading,
  } = useSWR<ThreadTableOfContents>(
    tocCacheKey,
    // Fetcher needs to construct the URL using any doc ID from the thread
    () => urlFetcher(`${API_URL}/doc/thread/${id}/toc`),
    {
      revalidateOnFocus: false, // Prevent refetch on tab switch - ToC is expensive to generate
      // Using default deduping interval (2s) so ToC updates when thread changes
    }
  );

  const docTree = useMemo(() => {
    if (!docs) return [];
    return buildDocTree(docs);
  }, [docs]);

  const [mainTopic, setMainTopic] = useState<DisplayConceptRef | null>(null);

  // Find the latest doc in the thread for replying
  const latestDocId = useMemo(() => {
    if (!docs || docs.length === 0) return null;

    // The docs are in chronological order, so the last one is the latest
    return docs[docs.length - 1].id;
  }, [docs]);

  // Compose Pathway #1: Register callback for OmniBox clicks
  // When user clicks the OmniBox on a doc page, it calls the registered callback.
  // This callback routes the reply to the latest message in the thread.
  // The callback is registered/updated whenever latestDocId changes.
  useEffect(() => {
    const handleComposeAtLatest = () => {
      if (latestDocId) {
        setReplyToDocId(latestDocId);
      }
    };

    setOnComposeAtLatest(handleComposeAtLatest);

    // Clean up callback when component unmounts or before re-registering
    return () => {
      setOnComposeAtLatest(null);
    };
  }, [latestDocId, setReplyToDocId, setOnComposeAtLatest]);

  useEffect(() => {
    const fetchMainTopic = async () => {
      if (!docs || docs.length === 0 || !docs[0].topic) {
        setMainTopic(null);
        return;
      }

      // Pick the most common topic (for now, should only be 1 anyway!)
      const topic_counts = docs.reduce(
        (acc: Record<string, { count: number; topic: DisplayConceptRef }>, doc: DisplayDoc) => {
          if (doc.topic) {
            const key = doc.topic.cref;
            if (!acc[key]) {
              acc[key] = { count: 0, topic: doc.topic };
            }
            acc[key].count++;
          }
          return acc;
        },
        {}
      );

      let most_common_topic = docs[0].topic; // Default to first doc's topic
      if (Object.keys(topic_counts).length > 1) {
        // console.log("Surprise multiple topics in doc thread: ",
        //   docs.map(doc => doc.id + " " + doc.topic?.cref));
        most_common_topic = Object.values(topic_counts).reduce((a, b) =>
          a.count > b.count ? a : b
        ).topic;
      }
      setMainTopic(most_common_topic);
    };
    fetchMainTopic();
  }, [docs]);

  const hasProducts = useMemo(() => {
    if (!docs || !userInfo) return false;

    return docs.some((doc) => {
      // Check if doc is a review/question about a product that's starred
      if (
        doc.topic &&
        doc.topic.is_product &&
        (doc.aspect?.cref === ASPECT_REVIEW || doc.aspect?.cref === ASPECT_QUESTION)
      ) {
        const isInCollection = userInfo.collections.some((collection) =>
          collection.products.some((p) => p.cref === doc.topic!.cref)
        );
        if (isInCollection) return true;
      }

      // Or if doc mentions any products that are starred
      if (doc.mentioned_concepts) {
        return doc.mentioned_concepts.some((concept) => {
          if (concept.is_product) {
            return userInfo.collections.some((collection) =>
              collection.products.some((p) => p.cref === concept.cref)
            );
          }
          return false;
        });
      }
      return false;
    });
  }, [docs, userInfo]);

  // Check if we should show the LiveContext (has products or table of contents)
  if (error)
    return (
      <p className="flex-item" role="alert">
        Failed to load documents
      </p>
    );
  if (isLoading)
    return (
      <Container maxWidth="md" sx={{ py: PAGE_PY.detail }}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {/* Topic header skeleton */}
          <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />

          {/* Main doc skeleton */}
          <Box>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
          </Box>

          {/* Reply skeletons */}
          {[...Array(2)].map((_, index) => (
            <Box key={index} sx={{ ml: 4 }}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 1 }} />
              <Skeleton variant="text" width="70%" />
            </Box>
          ))}
        </Stack>
      </Container>
    );
  if (!docs)
    return (
      <p className="flex-item" role="alert">
        No documents found
      </p>
    );

  return (
    <>
      <Container maxWidth="md" sx={{ py: PAGE_PY.detail }}>
        <Box component="h1" sx={{ mt: '0.5em', mb: '0.5em' }}>
          {/*
          The topic link on this page links to guides, which doesn't work if
          the main topic is a product.  So we suppress product links here.
          TODO(2): generalize
          */}
          {mainTopic && !mainTopic.is_product && (
            <Link href={topic_url(mainTopic.cref)} aria-label={`Topic: ${mainTopic.display}`}>
              {mainTopic.display}
            </Link>
          )}
        </Box>
        <Box component="section" sx={{ width: '100%' }} aria-label="Document tree">
          {docTree.map((node, index) => (
            <DocTree
              key={node.id}
              node={node}
              level={0}
              mainDocId={id ?? docs[0].id}
              mutate={refreshThread}
              isInFeedbackSubtree={false}
            />
          ))}
        </Box>
      </Container>
      {docs && (
        <LiveContext
          docs={docs}
          tableOfContents={tableOfContents}
          isAnalyzingToC={!!tocCacheKey && tocLoading}
        />
      )}
    </>
  );
};

export default DocPage;
